import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { dsClass } from '../../../../common/constants/dsClasses';
import {
  addMsg,
  elisaDevicesServiceMsg,
  oneTimePaymentMsg,
  quantityMsg,
  removeMsg,
  subtractMsg,
  t,
} from '../../../../common/i18n';
import { CommercialProductSubType } from '../../../../generated/api/commercialProductSubType';
import { OnlineModelCategory } from '../../../../generated/api/onlineModelCategory';
import { ProductDetailsAvailability } from '../../../ProductDetails/partials/ProductDetailsAvailability';
import { DisclaimerFields } from '../../../ShoppingBasket/DisclaimerFields';
import { getProductImage } from '../../../ShoppingBasket/shoppingBasketUiUtils';
import {
  getBadge,
  getCampaignAssociation,
  getCommercialProductForGuid,
  getEffectivePrice,
  getIsVoucherItem,
  getListPrice,
  getOfferFixedTermPeriod,
  getOnetimePaymentText,
  getPriceToDisplay,
} from '../../../ShoppingBasket/shoppingBasketUtils';
import type { BasketItem } from '../../../../common/types/shoppingBasket';
import type { Offer } from '../../../../generated/api/offer';
import type { OnlineModel } from '../../../../generated/api/onlineModel';
import type { CampaignData } from '../../placeOrderTypes';
import type { JSX } from 'react';

interface ProductEntryViewProps {
  productName: string;
  productImage: JSX.Element;
  description?: JSX.Element;
  price: JSX.Element;
  disclaimerFields: JSX.Element;
  quantityField?: JSX.Element;
  readOnly?: boolean;
}

export const ProductEntryView = (props: ProductEntryViewProps) => {
  return (
    <div className={classNames('of-place-order-product-entry', dsClass.SHOPPINGCART_PRODUCT_INFO)}>
      <div aria-hidden="true" className={dsClass.SHOPPINGCART_PRODUCT_IMAGE}>
        {props.productImage}
      </div>
      <div className={dsClass.SHOPPINGCART_PRODUCT_DETAILS}>
        <div className={dsClass.SHOPPINGCART_PRODUCT_NAME_AND_DESCRIPTION}>
          <h3 className={dsClass.SHOPPINGCART_PRODUCT_NAME}>{props.productName}</h3>
          <div className={dsClass.SHOPPINGCART_PRODUCT_DESCRIPTION}>{props.description}</div>
        </div>
        <div className={dsClass.SHOPPINGCART_PRODUCT_PRICE_AND_DISCLAIMER}>
          {!props.readOnly && props.quantityField && (
            <div className={classNames(dsClass.SHOPPINGCART_PRODUCT_QUANTITY, dsClass.MARGIN_BOTTOM_2)}>
              {props.quantityField}
            </div>
          )}
          <div className={dsClass.SHOPPINGCART_PRODUCT_PRICE}>{props.price}</div>
          <div className={dsClass.SHOPPINGCART_PRODUCT_DISCLAIMER}>{props.disclaimerFields}</div>
        </div>
      </div>
    </div>
  );
};

export interface ProductEntryProps {
  basketItem: BasketItem;
  offer: Offer;
  onlineModel: OnlineModel;
  changeQuantity: (id: string, quantity: number) => void;
  campaignData?: CampaignData;
  readOnly?: boolean;
  allModels?: OnlineModel[];
}

export const ProductEntry = ({
  basketItem,
  offer,
  onlineModel,
  changeQuantity,
  campaignData,
  readOnly,
  allModels,
}: ProductEntryProps) => {
  const productImage = getProductImage(basketItem.imageUrl, basketItem.name, offer, onlineModel);
  const priceData = getEffectivePrice(basketItem, onlineModel, allModels);
  const displayPrice = getPriceToDisplay(priceData, basketItem.quantity);
  const priceElement = (
    <>
      <output className={dsClass.SHOPPINGCART_PRICE_AMOUNT}>{displayPrice.amount}</output>
      <span className={dsClass.SHOPPINGCART_PRICE_UNIT}>{displayPrice.unit}</span>
    </>
  );

  const quantityField = (
    <CL.Quantity
      i18n_quantity_deleteAriaLabel={t.R3VE(removeMsg)}
      i18n_quantity_minusAriaLabel={t.C2KQ(subtractMsg)}
      i18n_quantity_plusAriaLabel={t.VKFM(addMsg)}
      i18n_quantity_quantityAriaLabel={t.M0W7(quantityMsg)}
      className={dsClass.SHOPPINGCART_PRODUCT_QUANTITY}
      currentValue={basketItem.quantity}
      minValue={1}
      onChange={(quantity: number) => {
        if (quantity !== basketItem.quantity) {
          changeQuantity(basketItem.id, quantity);
        }
      }}
      onRemove={() => {
        changeQuantity(basketItem.id, 0);
      }}
      removeLessThanMin
    />
  );

  const canHaveActivationFee = [OnlineModelCategory.SALES_PRODUCT, OnlineModelCategory.SELF_SERVICE].includes(
    onlineModel.category as OnlineModelCategory
  );

  const oneTimePaymentText = getOnetimePaymentText(
    priceData?.price?.oneTimeCharge,
    basketItem.quantity,
    canHaveActivationFee
  );

  const isVoucherItem = getIsVoucherItem(basketItem, onlineModel, campaignData?.voucherCode);
  const listPrice = getListPrice(basketItem, onlineModel);

  const priceDisclaimers = (
    <DisclaimerFields
      oneTimePaymentText={oneTimePaymentText}
      priceData={priceData}
      listPrice={isVoucherItem || priceData?.isContractPrice ? listPrice : undefined}
      badge={getBadge(isVoucherItem, true, priceData, campaignData?.voucherCode)}
      isVoucherItem={isVoucherItem}
      quantity={basketItem.quantity}
    />
  );

  const cp = getCommercialProductForGuid(basketItem.offer.commercialProductGuid, onlineModel);
  const productSubType = cp?.productSubType;
  const isDevice =
    productSubType && [CommercialProductSubType.DEVICE, CommercialProductSubType.EPP_DEVICE].includes(productSubType);
  const isEpp = cp?.productSubType === CommercialProductSubType.EPP_DEVICE;

  const paymentsElement = (() => {
    const payments = priceData?.price?.payments;
    if (payments && payments > 1) {
      const paymentsStr = payments.toString();
      return isEpp
        ? t.BCWX(`${elisaDevicesServiceMsg}, {} month contract`, paymentsStr)
        : t.RA9V('Term of payment {} Month', paymentsStr);
    }
    if (priceData?.price?.oneTimeCharge) {
      return t.ASEI(oneTimePaymentMsg);
    }
    return '';
  })();

  const campaignAssociation = getCampaignAssociation(
    campaignData?.voucherCode ?? '',
    campaignData?.campaignsFromVoucher ?? [],
    campaignData?.campaignContextsFromVoucher ?? [],
    onlineModel,
    offer,
    basketItem.offer.commercialProductGuid
  );

  const quantityText = t.BDBD('{} pcs', String(basketItem.quantity));
  const fixedTermPeriod = getOfferFixedTermPeriod(offer, campaignAssociation);

  const fixedTermText = fixedTermPeriod && t.IWHR('{} month fixed term contract', `${fixedTermPeriod}`);

  const description = (() => {
    if (isVoucherItem) {
      if (readOnly) {
        return <span>{fixedTermText ? `${fixedTermText}, ${quantityText}` : quantityText}</span>;
      }
      return <span>{fixedTermText}</span>;
    }
    if (isDevice && offer) {
      if (readOnly) {
        return (
          <span className={classNames(dsClass.FONT_SIZE_SMALL)}>
            {paymentsElement ? `${paymentsElement}, ${quantityText}` : quantityText}
          </span>
        );
      }
      return (
        <>
          <ProductDetailsAvailability offer={offer} />
          <span className={classNames(dsClass.FONT_SIZE_SMALL)}>{paymentsElement}</span>
        </>
      );
    }
    // Non-voucher subscription items: show fixed term text if campaign has fixedTermPeriod
    if (fixedTermText) {
      if (readOnly) {
        return <span>{`${fixedTermText}, ${quantityText}`}</span>;
      }
      return <span>{fixedTermText}</span>;
    }
    if (readOnly) {
      return <span>{quantityText}</span>;
    }
    return undefined;
  })();

  return (
    <ProductEntryView
      productName={basketItem.name}
      productImage={productImage}
      description={description}
      price={priceElement}
      quantityField={quantityField}
      disclaimerFields={priceDisclaimers}
      readOnly={readOnly}
    />
  );
};
